import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import logger from '../../lib/logger';
import { getApiBaseUrl } from '../../lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAnimateIn, useStaggeredAnimateIn } from '@/lib/useAnimateIn';
import { trackEvent } from '@/lib/analytics';
import CTAButton from '../ui/CTAButton';

interface FormViewProps {
  setCurrentView?: (view: number) => void;
  selectedOptions?: string[];
}

export default function FormView({ setCurrentView, selectedOptions = [] }: FormViewProps) {
  const pathname = usePathname();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    additionalInfo: ''
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [attemptedSteps, setAttemptedSteps] = useState<Set<number>>(new Set());

  const totalSteps = 4;
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Progress bar animation - opacity only
  const [progressBarVisible, setProgressBarVisible] = useState(false);
  
  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          setProgressBarVisible(true);
        }, 100);
      });
    });
  }, []);
  
  const progressBarStyle: React.CSSProperties = {
    transition: 'opacity 500ms ease-out',
    opacity: progressBarVisible ? 1 : 0,
    transform: 'none' // No translateY animation
  };

  // Staggered animation for step 1
  const { getItemStyle: getStep1Style, trigger: triggerStep1, reset: resetStep1 } = useStaggeredAnimateIn({
    delay: 300,
    staggerDelay: 80,
    autoStart: false
  });

  // Staggered animations for steps 2, 3, and 4
  const { getItemStyle: getStep2Style, trigger: triggerStep2, reset: resetStep2 } = useStaggeredAnimateIn({
    delay: 0,
    staggerDelay: 80,
    autoStart: false
  });
  const { getItemStyle: getStep3Style, trigger: triggerStep3, reset: resetStep3 } = useStaggeredAnimateIn({
    delay: 0,
    staggerDelay: 80,
    autoStart: false
  });
  const { getItemStyle: getStep4Style, trigger: triggerStep4, reset: resetStep4 } = useStaggeredAnimateIn({
    delay: 0,
    staggerDelay: 80,
    autoStart: false
  });

  // Fade-only animations for each step
  const [step1Visible, setStep1Visible] = useState(false);
  const [step2Visible, setStep2Visible] = useState(false);
  const [step3Visible, setStep3Visible] = useState(false);
  const [step4Visible, setStep4Visible] = useState(false);

  // Trigger step 1 animation when on step 1
  useEffect(() => {
    if (currentStep === 1) {
      triggerStep1();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setStep1Visible(true);
        });
      });
    } else {
      resetStep1();
      setStep1Visible(false);
    }
  }, [currentStep, triggerStep1, resetStep1]);

  // Trigger fade animations for other steps
  useEffect(() => {
    if (currentStep === 2) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setStep2Visible(true);
          triggerStep2();
        });
      });
    } else {
      setStep2Visible(false);
      resetStep2();
    }
  }, [currentStep, triggerStep2, resetStep2]);

  useEffect(() => {
    if (currentStep === 3) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setStep3Visible(true);
          triggerStep3();
        });
      });
    } else {
      setStep3Visible(false);
      resetStep3();
    }
  }, [currentStep, triggerStep3, resetStep3]);

  useEffect(() => {
    if (currentStep === 4) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setStep4Visible(true);
          triggerStep4();
          
          // Restore textarea height based on content
          setTimeout(() => {
            const textarea = document.querySelector('textarea[aria-labelledby="additional-label"]') as HTMLTextAreaElement;
            if (textarea && formData.additionalInfo) {
              textarea.style.height = 'auto';
              const newHeight = textarea.scrollHeight;
              const computedStyle = window.getComputedStyle(textarea);
              const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.5;
              const maxHeight = lineHeight * 3; // 3 lines
              
              if (newHeight <= maxHeight) {
                textarea.style.height = newHeight + 'px';
                textarea.style.overflowY = 'hidden';
              } else {
                textarea.style.height = maxHeight + 'px';
                textarea.style.overflowY = 'auto';
              }
            }
          }, 100);
        });
      });
    } else {
      setStep4Visible(false);
      resetStep4();
    }
  }, [currentStep, triggerStep4, resetStep4, formData.additionalInfo]);

  // Focus input field when step changes and prevent h2 focus
  useEffect(() => {
    // Remove focus from any h2 elements that might have gotten focused
    const h2Elements = document.querySelectorAll('h2[id*="-label"]');
    h2Elements.forEach((h2) => {
      const element = h2 as HTMLElement;
      if (document.activeElement === element) {
        element.blur();
      }
      // Prevent any focus behavior
      element.setAttribute('contenteditable', 'false');
      element.style.caretColor = 'transparent';
    });
    
    // Focus the input field for the current step after animation
    setTimeout(() => {
      let inputElement: HTMLInputElement | HTMLTextAreaElement | null = null;
      
      if (currentStep === 1) {
        inputElement = document.querySelector('input[aria-labelledby="name-label"]') as HTMLInputElement;
      } else if (currentStep === 2) {
        inputElement = document.querySelector('input[aria-labelledby="company-label"]') as HTMLInputElement;
      } else if (currentStep === 3) {
        inputElement = document.querySelector('input[aria-labelledby="email-label"]') as HTMLInputElement;
      } else if (currentStep === 4) {
        inputElement = document.querySelector('textarea[aria-labelledby="additional-label"]') as HTMLTextAreaElement;
      }
      
      if (inputElement) {
        inputElement.focus();
      }
    }, 100);
  }, [currentStep]);

  // Fade-only styles
  const step1Style: React.CSSProperties = {
    transition: 'opacity 300ms ease-out',
    opacity: step1Visible ? 1 : 0
  };
  const step2Style: React.CSSProperties = {
    transition: 'opacity 300ms ease-out',
    opacity: step2Visible ? 1 : 0
  };
  const step3Style: React.CSSProperties = {
    transition: 'opacity 300ms ease-out',
    opacity: step3Visible ? 1 : 0
  };
  const step4Style: React.CSSProperties = {
    transition: 'opacity 300ms ease-out',
    opacity: step4Visible ? 1 : 0
  };

  const submitForm = async () => {
    // Track analytics for submit button
    trackEvent('ai_click', {
      click_text: 'Get in touch - submit',
      page_path: pathname
    });
    
    const API_BASE_URL = getApiBaseUrl();
    const API_KEY = process.env.NEXT_PUBLIC_API_KEY;
    
    logger.info('Form submission data:', {
      selectedOptions,
      name: formData.name,
      company: formData.company,
      email: formData.email,
      additionalInfo: formData.additionalInfo
    });
    
    if (!API_BASE_URL || !API_KEY) {
      logger.error('Missing API configuration. Please set NEXT_PUBLIC_API_URL and NEXT_PUBLIC_API_KEY environment variables.');
      return;
    }
    
    try {
      // Map the form data to the expected API format
      const contactData = {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        message: formData.additionalInfo.trim() || ''
      };
      
      logger.info('Making API call to contact endpoint');
      
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactData)
      });
      
      logger.info('Contact response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('API Error response:', errorText);
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }
      
      await response.json();
      logger.info('Contact submission successful');
      
      // On success, navigate to success view
      setCurrentView?.(4);
      
    } catch (error) {
      logger.error('Contact API Error:', error);
      setFormError('Sorry, there was an error submitting your request. Please try again.');
    }
  };

  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    // Mark current step as attempted
    setAttemptedSteps(prev => new Set(prev).add(currentStep));
    
    // Track analytics for name panel (step 1)
    if (currentStep === 1 && isCurrentStepValid()) {
      trackEvent('ai_click', {
        click_text: 'Get in touch - name',
        page_path: pathname
      });
    }
    
    // Track analytics for work panel (step 2)
    if (currentStep === 2 && isCurrentStepValid()) {
      trackEvent('ai_click', {
        click_text: 'Get in touch - work',
        page_path: pathname
      });
    }
    
    // Track analytics for email contact panel (step 3)
    if (currentStep === 3 && isCurrentStepValid()) {
      trackEvent('ai_click', {
        click_text: 'Get in touch - contact',
        page_path: pathname
      });
    }
    
    if (currentStep < totalSteps) {
      // Only proceed if current step is valid
      if (isCurrentStepValid()) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      // Submit form on last step
      submitForm();
    }
  };

  const handlePrevious = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    // Mark current step as attempted
    setAttemptedSteps(prev => new Set(prev).add(currentStep));
    
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateFormData = (field: string, value: string) => {
    // For email field, prevent typing if TLD would exceed 63 characters
    if (field === 'email') {
      const tldLength = getTldLength(value);
      if (tldLength !== null && tldLength > 63) {
        // Don't update if TLD would exceed 63 characters
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Mark the current step as attempted when user starts typing
    if (value.trim() !== '' && !attemptedSteps.has(currentStep)) {
      setAttemptedSteps(prev => new Set(prev).add(currentStep));
    }
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return false;
    }
    
    // Extract TLD (top-level domain) - the part after the last dot
    const parts = email.split('@');
    if (parts.length !== 2) {
      return false;
    }
    
    const domain = parts[1];
    const domainParts = domain.split('.');
    if (domainParts.length < 2) {
      return false;
    }
    
    const tld = domainParts[domainParts.length - 1];
    // TLD length must be between 2 and 63 characters
    return tld.length >= 2 && tld.length <= 63;
  };

  const getTldLength = (email: string): number | null => {
    const parts = email.split('@');
    if (parts.length !== 2) {
      return null;
    }
    
    const domain = parts[1];
    const domainParts = domain.split('.');
    if (domainParts.length < 2) {
      return null;
    }
    
    return domainParts[domainParts.length - 1].length;
  };

  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.name.trim() !== '';
      case 2:
        return formData.company.trim() !== '';
      case 3:
        return isValidEmail(formData.email);
      case 4:
        return true; // Optional field
      default:
        return false;
    }
  };

  return (
    <div className='view-form view-3 flex flex-col h-full py-10 px-2 lg:px-32'>
      {formError && (
        <div role="alert" aria-live="assertive" className="mx-6 lg:mx-[10rem] mb-4 p-3 border border-red-400 bg-red-50 text-red-700 rounded">
          {formError}
        </div>
      )}
      {/* Progress Bar */}
      <div className='px-6 lg:px-[10rem]' style={progressBarStyle}>
        <div className="w-full h-1 bg-gray-200 mb-12 rounded-full">
          <div 
            className="h-full bg-red-600 transition-all duration-300 rounded-full"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 flex flex-col">
        <div>
          {currentStep === 1 && (
            <div style={step1Style}>
              <h2 
                id="name-label" 
                className="card-title-lg mb-8" 
                tabIndex={-1} 
                contentEditable={false}
                onFocus={(e) => e.target.blur()}
                style={{ ...getStep1Style(0), transform: 'none', outline: 'none', userSelect: 'none', caretColor: 'transparent' }}
              >
                What&rsquo;s your name?
              </h2>
              <input
                type="text"
                aria-labelledby="name-label"
                aria-required="true"
                aria-invalid={formData.name.trim() === ''}
                aria-describedby={attemptedSteps.has(1) && formData.name.trim() === '' ? 'name-error' : undefined}
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                placeholder="Name"
                className="w-full body-sm text-[#333333] bg-transparent border-b-2 border-gray-300 focus:border-gray-900 outline-none pb-2 transition-colors"
                style={{ ...getStep1Style(1), transform: 'none' }}
              />
              {attemptedSteps.has(1) && formData.name.trim() === '' && (
                <p id="name-error" className="mt-2 body-sm text-red-600" role="alert" style={{ ...getStep1Style(2), transform: 'none' }}>Name is required.</p>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div style={step2Style}>
              <h2 
                id="company-label" 
                className="card-title-lg mb-8" 
                tabIndex={-1} 
                contentEditable={false}
                onFocus={(e) => e.target.blur()}
                style={{ ...getStep2Style(0), transform: 'none', outline: 'none', userSelect: 'none', caretColor: 'transparent' }}
              >
                Where do you work?
              </h2>
              <input
                type="text"
                aria-labelledby="company-label"
                aria-required="true"
                aria-invalid={formData.company.trim() === ''}
                aria-describedby={attemptedSteps.has(2) && formData.company.trim() === '' ? 'company-error' : undefined}
                value={formData.company}
                onChange={(e) => updateFormData('company', e.target.value)}
                placeholder="Company"
                className="w-full body-sm text-[#333333] bg-transparent border-b-2 border-gray-300 focus:border-gray-900 outline-none pb-2 transition-colors"
                style={{ ...getStep2Style(1), transform: 'none' }}
              />
              {attemptedSteps.has(2) && formData.company.trim() === '' && (
                <p id="company-error" className="mt-2 body-sm text-red-600" role="alert">Company is required.</p>
              )}
            </div>
          )}

          {currentStep === 3 && (
            <div style={step3Style}>
              <h2 
                id="email-label" 
                className="card-title-lg mb-8" 
                tabIndex={-1} 
                contentEditable={false}
                onFocus={(e) => e.target.blur()}
                style={{ ...getStep3Style(0), transform: 'none', outline: 'none', userSelect: 'none', caretColor: 'transparent' }}
              >
                Where should we contact you?
              </h2>
              <input
                type="email"
                aria-labelledby="email-label"
                aria-required="true"
                aria-invalid={!isValidEmail(formData.email)}
                aria-describedby={attemptedSteps.has(3) && formData.email.trim() !== '' && !isValidEmail(formData.email) ? 'email-error' : undefined}
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
                placeholder="Email"
                className="w-full body-sm text-[#333333] bg-transparent border-b-2 border-gray-300 focus:border-gray-900 outline-none pb-2 no-autofill-bg"
                style={{ ...getStep3Style(1), transform: 'none' }}
              />
              {attemptedSteps.has(3) && formData.email.trim() !== '' && !isValidEmail(formData.email) && (
                <p id="email-error" className="mt-2 text-sm text-red-600" role="alert">Enter a valid email address.</p>
              )}
            </div>
          )}

          {currentStep === 4 && (
            <div style={step4Style}>
              <h2 
                id="additional-label" 
                className="card-title-lg mb-8" 
                tabIndex={-1} 
                contentEditable={false}
                onFocus={(e) => e.target.blur()}
                style={{ ...getStep4Style(0), transform: 'none', outline: 'none', userSelect: 'none', caretColor: 'transparent' }}
              >
                Anything else you want us to know?
              </h2>
              <textarea
                aria-labelledby="additional-label"
                value={formData.additionalInfo}
                onChange={(e) => {
                  updateFormData('additionalInfo', e.target.value);
                  const textarea = e.target;
                  textarea.style.height = 'auto';
                  const newHeight = textarea.scrollHeight;
                  // Calculate max height for 3 lines using computed styles
                  const computedStyle = window.getComputedStyle(textarea);
                  const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.5;
                  const maxHeight = lineHeight * 3; // 3 lines
                  
                  if (newHeight <= maxHeight) {
                    textarea.style.height = newHeight + 'px';
                    textarea.style.overflowY = 'hidden';
                  } else {
                    textarea.style.height = maxHeight + 'px';
                    textarea.style.overflowY = 'auto';
                  }
                }}
                placeholder="Additional information (optional)"
                rows={1}
                className="w-full body-sm text-[#333333] bg-transparent border-b-2 border-gray-300 focus:border-gray-900 outline-none pb-2 transition-colors resize-none"
                style={{ ...getStep4Style(1), transform: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-10">
          {currentStep > 1 ? (
            <button
              onClick={handlePrevious}
              className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-all duration-300 ease-out hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <ChevronLeft size={24} />
            </button>
          ) : (
            <div className="w-12 h-12" />
          )}

          {currentStep < totalSteps ? (
            <button
              onClick={handleNext}
              disabled={!isCurrentStepValid()}
              className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center transition-all duration-300 ease-out hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-red-600 disabled:hover:scale-100"
            >
              <ChevronRight size={24} />
            </button>
          ) : (
            <CTAButton onClick={submitForm}>
              Submit
            </CTAButton>
          )}
        </div>
      </div>
    </div>
  );
}
